import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    SecurityContext,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { QuillModules } from 'ngx-quill';
import { ContentChange } from 'ngx-quill';
import { BusyIndicatorMessageManager } from '../../Busy-Indicator';
import { AppealRecommendationRepository } from '../appealRecommendation.repository';
import { lastValueFrom, Subject } from 'rxjs';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { HelpContentComponentConfig, HelpService } from '../../UI-Lib/Help-Tooltip';
import { APPEAL_RECOMMENDATION_HELP } from './appealRecommendationTemplate.component.help';
import { DownloadFileService } from '../../Common/Download/downloadFileService';
import {
    MessageBoxButtons,
    MessageBoxResult,
    MessageBoxService,
} from '../../UI-Lib/Message-Box/messagebox.service.upgrade';
import { CommentsModalService } from '../../Comments/commentsModal.service';
import { CommentModalData } from '../../Comments/comments.service';
import { CommentTypeNames, EntityTypeIds } from '../../constants.new';
import { ToastrService } from 'ngx-toastr';
import { TimerService, UtilitiesService } from '../../UI-Lib/Utilities';
import {
    AppealRecommendationTemplateLastSentHelpComponent,
    AppealRecommendationTemplateLastSentHelpComponentParams,
} from './appealRecommendationTemplateLastSentHelp.component';
import { WeissmanModalService } from '../../Compliance/WeissmanModalService';
import { CommentDateTimeFormat } from '../../UI-Lib/Pipes';
import { UserSettingsService } from '../../Account/userSettings.service';
import {
    AppealRecommendationTemplateDetailsComponent,
    AppealRecommendationTemplateDetailsParams,
} from './Appeal-Recommendation-Template-Details/appealRecommendationTemplateDetails.component';
import { RestrictService, Roles } from '../../Common/Permissions/restrict.service';
import { MessageModalService } from '../../UI-Lib/Message-Box/messageModal.service';
import { AppealRecommendationTemplateService } from './appealRecommendationTemplate.service';
import { takeUntil } from 'rxjs/operators';

declare const $: any;

enum PanelView {
    Options,
    Attachments,
}

export class TemplateFieldDTO {
    name: string;
    validInSubject: boolean;
}

export interface VisibilityModel {
    name: string;
    isDefault: boolean;
    isShared: boolean;
    description: string;
}

export interface AppealRecommendationEmailTemplateListItemModel extends Core.AppealRecommendationEmailTemplateModel {
    canEdit: boolean;
    visibilityItem: VisibilityModel;
}

@Component({
    templateUrl: './appealRecommendationTemplate.component.html',
    selector: 'appeal-recommendation-template',
    styleUrls: ['./appealRecommendationTemplate.component.scss'],
    providers: [AppealRecommendationTemplateService],
    standalone: false,
    encapsulation: ViewEncapsulation.None,
})
export class AppealRecommendationTemplateComponent implements OnInit, OnDestroy {
    constructor(
        private readonly _bsModalRef: BsModalRef,
        private readonly _appealRecommendationRepository: AppealRecommendationRepository,
        private readonly _sanitizer: DomSanitizer,
        private readonly _helpService: HelpService,
        private readonly _downloadFileService: DownloadFileService,
        private readonly _messageBoxService: MessageBoxService,
        private readonly _commentsModalService: CommentsModalService,
        private readonly _toastsManager: ToastrService,
        private readonly _timer: TimerService,
        private readonly _modalService: WeissmanModalService,
        private readonly _commentDateTimePipe: CommentDateTimeFormat,
        private readonly _userSettingsService: UserSettingsService,
        private readonly _arTemplateService: AppealRecommendationTemplateService,
        private readonly _cdRef: ChangeDetectorRef,
        private readonly _restrictService: RestrictService,
        private readonly _messageModalService: MessageModalService,
    ) {}

    @ViewChild('optionsPanel1') set optionsPanel1(p: ElementRef) {
        this._optionsPanel1 = p;
        this._setConsistentPanelWidth();
    }

    @ViewChild('optionsPanel2') set optionsPanel2(p: ElementRef) {
        this._optionsPanel2 = p;
        this._setConsistentPanelWidth();
    }

    panelSize = null;
    taskIds: number[];
    instanceId: number;
    busyIndicatorMessageManager = new BusyIndicatorMessageManager<string>();
    editorInstance: QuillModules;
    emailTemplateSubjectFields: TemplateFieldDTO[];
    emailTemplateBodyFields: TemplateFieldDTO[];
    editing: boolean = false;
    initialized: boolean = false;
    selectedView: PanelView = PanelView.Options;
    sites: Core.AppealRecommendationSiteInfoModel[];
    appealRecommendationInfo: Core.AppealRecommendationTemplateModel;
    // #587257 - Quill can fire onContentChanged on binding/re-render (not just user typing), and
    // for certain HTML shapes its semantic-HTML serialization is not stable between calls. If
    // contentChange() unconditionally reassigned the bound model on every event, that swap could
    // re-trigger another content-changed event, which could never settle (infinite loop / browser
    // freeze) - the same mechanism fixed for the transmittal email editor in #457535. Track the
    // most recent edit here instead, and only commit it to the model on save/refresh.
    mostRecentEmailBodyUpdate: string;
    emailBody: SafeHtml;
    lastSent: string;
    attachments: Core.AppealRecommendationSiteAttachmentModel[] = [];
    files: File[] = [];
    savedSettings: Core.AppealRecommendationTemplateOptionsModel = {
        includeSiteComment: true,
        showPriorYear: true,
        showYearBuilt: false,
        showAcquisitionInfo: true,
        showAppealDeadlines: true,
        showAssessments: Core.PsrShowAssessmentsEnum.FMV,
        showMetrics: Core.PsrShowMetrics.MostApplicableBasedOnSiteClass,
        showInitialValue: Core.PsrShowValueEnum.YesNoMetrics,
        showTargetValue: Core.PsrShowValueEnum.YesNoMetrics,
        showLatestValue: Core.PsrShowLatestValueEnum.YesInclMetricsAndYOYChange,
        showTaxDetails: Core.PsrShowTaxDetails.None,
        showSavings: Core.PsrShowSavings.No,
        embedInEmail: true,
        attachChart: Core.AppealRecommendationTemplateAttachChartOptionEnum.No,
        showTaxableValues: true,
        showEmptyColumns: false,
        showExemptions: true,
    };
    sentInfoTooltip: HelpContentComponentConfig<
        AppealRecommendationTemplateLastSentHelpComponent,
        AppealRecommendationTemplateLastSentHelpComponentParams
    > = {
        component: AppealRecommendationTemplateLastSentHelpComponent,
        componentParams: {
            sentInfo: null,
            totalParcelsCount: -1,
        },
    };
    showAssessmentOptions: Compliance.NameValuePair<Core.PsrShowAssessmentsEnum>[] = [];
    showMetricOptions: Compliance.NameValuePair<Core.PsrShowMetrics>[] = [
        { name: 'None', value: Core.PsrShowMetrics.None },
        { name: 'Most applicable based on site class', value: Core.PsrShowMetrics.MostApplicableBasedOnSiteClass },
        { name: 'All present', value: Core.PsrShowMetrics.AllPresent },
    ];
    showInitialValueOptions: Compliance.NameValuePair<Core.PsrShowValueEnum>[] = [
        { name: 'No', value: Core.PsrShowValueEnum.No },
        { name: 'Yes (Incl. Metrics)', value: Core.PsrShowValueEnum.YesInclMetrics },
        { name: 'Yes (No Metrics)', value: Core.PsrShowValueEnum.YesNoMetrics },
    ];
    showTargetValueOptions: Compliance.NameValuePair<Core.PsrShowValueEnum>[] = [
        { name: 'No', value: Core.PsrShowValueEnum.No },
        { name: 'Yes (Incl. Metrics)', value: Core.PsrShowValueEnum.YesInclMetrics },
        { name: 'Yes (No Metrics)', value: Core.PsrShowValueEnum.YesNoMetrics },
    ];
    showLatestValueOptions: Compliance.NameValuePair<Core.PsrShowLatestValueEnum>[] = [
        { name: 'Yes (Incl. Metrics and YOY % Change)', value: Core.PsrShowLatestValueEnum.YesInclMetricsAndYOYChange },
        { name: 'Yes (Incl. Metrics)', value: Core.PsrShowLatestValueEnum.YesInclMetrics },
        { name: 'Yes (Incl. YOY % Change )', value: Core.PsrShowLatestValueEnum.YesInclYOYChange },
        { name: 'Yes (No Metrics or YOY % Change)', value: Core.PsrShowLatestValueEnum.YesNoMetricsOrYOYChange },
    ];
    showTaxDetailsOptions: Compliance.NameValuePair<Core.PsrShowTaxDetails>[] = [
        { name: 'None', value: Core.PsrShowTaxDetails.None },
        { name: 'Latest Taxes (Incl. Metrics)', value: Core.PsrShowTaxDetails.LatestTaxesInclMetrics },
        { name: 'Latest Taxes (No Metrics)', value: Core.PsrShowTaxDetails.LatestTaxesNoMetrics },
        { name: 'All Taxes (Incl. Metrics)', value: Core.PsrShowTaxDetails.AllTaxesInclMetrics },
        { name: 'All Taxes (No Metrics)', value: Core.PsrShowTaxDetails.AllTaxesNoMetrics },
    ];
    showSavingOptions: Compliance.NameValuePair<Core.PsrShowSavings>[] = [
        { name: 'Received and Projected (Combined)', value: Core.PsrShowSavings.ReceivedAndProjectedCombined },
        { name: 'Received and Projected', value: Core.PsrShowSavings.ReceivedAndProjected },
        { name: 'Received Only', value: Core.PsrShowSavings.ReceivedOnly },
        { name: 'Projected Only', value: Core.PsrShowSavings.ProjectedOnly },
        { name: 'No', value: Core.PsrShowSavings.No },
    ];
    embedChartOptions: Compliance.NameValuePair<boolean>[] = [
        { name: 'Yes', value: true },
        { name: 'No', value: false },
    ];
    showExemptionsOptions: Compliance.NameValuePair<boolean>[] = [
        { name: 'Yes', value: true },
        { name: 'No', value: false },
    ];
    attachChartOptions: any[] = [
        { name: 'No', value: Core.AppealRecommendationTemplateAttachChartOptionEnum.No, isDisabled: false, title: '' },
        {
            name: 'PDF',
            value: Core.AppealRecommendationTemplateAttachChartOptionEnum.PDF,
            isDisabled: false,
            title: '',
        },
        {
            name: 'Excel',
            value: Core.AppealRecommendationTemplateAttachChartOptionEnum.Excel,
            isDisabled: false,
            title: '',
        },
    ];
    showTaxableValuesOptions: Compliance.NameValuePair<boolean>[] = [
        { name: 'Yes', value: true },
        { name: 'No', value: false },
    ];
    selectedTemplate: AppealRecommendationEmailTemplateListItemModel;
    templates: AppealRecommendationEmailTemplateListItemModel[];
    useTaxableValues: boolean = true;

    protected readonly PanelView = PanelView;
    private _optionsPanel1: ElementRef;
    private _optionsPanel2: ElementRef;
    private _appealRecommendationInfo: Core.AppealRecommendationTemplateModel;
    private _savedSettings: Core.AppealRecommendationTemplateOptionsModel;
    private _initialAppealRecommendationInfo: Core.AppealRecommendationTemplateModel;
    private _settingName: string = 'Selected-Options';
    private _savedUserSettings: any;
    private _attachments: Core.AppealRecommendationSiteAttachmentModel[] = [];
    private _siteDivisorDescriptors: System.Collections.Generic.KeyValuePair<number, number>[] = [];
    private _selectedTemplateId: number;
    private _hasEditSystemSearchPermission: boolean;
    private _destroy$: Subject<void> = new Subject();

    async ngOnInit(): Promise<void> {
        this._helpService.setContent(APPEAL_RECOMMENDATION_HELP);
        this._hasEditSystemSearchPermission = this._restrictService.isInRole(Roles.SYSTEMSEARCHESEDIT);

        this.savedSettings.showTaxableValues = true;

        this.showAssessmentOptions = [
            { name: 'MV', value: Core.PsrShowAssessmentsEnum.FMV },
            { name: 'Alternate', value: Core.PsrShowAssessmentsEnum.Alternate },
            {
                name: 'MV and Alternate',
                value: Core.PsrShowAssessmentsEnum.FMVAndAlternate,
            },
        ];

        this._arTemplateService.selectedAttachments$.pipe(takeUntil(this._destroy$)).subscribe((x) => {
            this.attachments = x
                .filter((y) => !(y.attachmentId as string).startsWith('_'))
                .map((y) => ({
                    attachmentId: y.attachmentId,
                    siteId: y.siteId,
                    attachmentName: `${y.attachmentName}.${y.fileExtension}`,
                }));
            this.files = x.filter((y) => y.file).map((y) => y.file);
        });

        try {
            const templates = await lastValueFrom(this._appealRecommendationRepository.getTemplatesAsync(this.taskIds));

            this.templates = templates.map((x) => this._convertToListItem(x));
            this._sortSavedTemplates();

            await this._loadSettings();

            const model: Core.AppealRecommendationTemplateRefreshModel = {
                taskIds: this.taskIds,
                emailSubjectTemplate: null,
                emailBodyTemplate: null,
                showPriorYear: this.savedSettings.showPriorYear,
                embedInEmail: this.savedSettings.embedInEmail,
                includeSiteComment: this.savedSettings.includeSiteComment,
                recipientFirstNames: [],
                showAssessments: this.savedSettings.showAssessments,
                showMetrics: this.savedSettings.showMetrics,
                showInitialValue: this.savedSettings.showInitialValue,
                showTargetValue: this.savedSettings.showTargetValue,
                showLatestValue: this.savedSettings.showLatestValue,
                showTaxDetails: this.savedSettings.showTaxDetails,
                showSavings: this.savedSettings.showSavings,
                showYearBuilt: this.savedSettings.showYearBuilt,
                showAcquisitionInfo: this.savedSettings.showAcquisitionInfo,
                showAppealDeadlines: this.savedSettings.showAppealDeadlines,
                attachChart: this.savedSettings.attachChart,
                showTaxableValues: this.savedSettings.showTaxableValues,
                showEmptyColumns: this.savedSettings.showEmptyColumns,
                showExemptions: this.savedSettings.showExemptions,
            };

            model.emailSubjectTemplate = this.selectedTemplate.emailSubjectTemplate;
            model.emailBodyTemplate = this.selectedTemplate.emailBodyTemplate;

            const requests = await Promise.all([
                lastValueFrom(this._appealRecommendationRepository.getTemplate(model)),
                lastValueFrom(this._appealRecommendationRepository.getFields()),
            ]);

            this.appealRecommendationInfo = requests[0];
            this._initialAppealRecommendationInfo = { ...this._initialAppealRecommendationInfo };
            this._siteDivisorDescriptors = this.appealRecommendationInfo.siteDivisors;

            let fields = requests[1];

            if (this.appealRecommendationInfo.hasMultipleSites) {
                fields = fields.filter((x) => x.name !== 'CompanyName' && x.name !== 'SiteName');
            }

            if (this.appealRecommendationInfo.hasMultipleAssessors) {
                fields = fields.filter((x) => x.name !== 'Jurisdiction' && x.name !== 'AppealDeadline');
            }

            this.emailTemplateSubjectFields = fields.filter((x) => x.validInSubject);
            this.emailTemplateBodyFields = fields;

            this.sites = this.appealRecommendationInfo.sites.sort((x, y) => x.siteName.localeCompare(y.siteName));

            this.emailBody = this._sanitizer.sanitize(SecurityContext.HTML, this.appealRecommendationInfo.emailBody);

            if (this.appealRecommendationInfo.sentInfo.length !== 0) {
                const lastSentInfo = this.appealRecommendationInfo.sentInfo
                    .sort((a, b) => b.sentDate.getTime() - a.sentDate.getTime())
                    .at(0);

                if (lastSentInfo) {
                    this.lastSent = `${lastSentInfo.userLastName}, ${lastSentInfo.userFirstName} ${this._commentDateTimePipe.transform(lastSentInfo.sentDate)}`;
                }
                this.sentInfoTooltip.componentParams.sentInfo = this.appealRecommendationInfo.sentInfo;
                this.sentInfoTooltip.componentParams.totalParcelsCount = this.appealRecommendationInfo.parcelsCount;
            }
        } finally {
            this.initialized = true;
        }
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    async close(): Promise<void> {
        if (!this.appealRecommendationInfo) {
            this._bsModalRef.hide();
            return;
        }
        // Compare against mostRecentEmailBodyUpdate (if set) rather than appealRecommendationInfo.emailBodyTemplate
        // directly - the body field is no longer live-updated on every keystroke (see contentChange()/#587257 fix),
        // so an in-progress, not-yet-saved body edit would otherwise go undetected here.
        const currentEmailBodyTemplate = this.mostRecentEmailBodyUpdate ?? this.appealRecommendationInfo.emailBodyTemplate;
        let hasNotSavedChanges: boolean =
            JSON.stringify(this._initialAppealRecommendationInfo.emailSubjectTemplate) !==
                JSON.stringify(this.appealRecommendationInfo.emailSubjectTemplate) ||
            JSON.stringify(this._initialAppealRecommendationInfo.emailBodyTemplate) !==
                JSON.stringify(currentEmailBodyTemplate);

        if (!hasNotSavedChanges) {
            hasNotSavedChanges =
                this.savedSettings.showPriorYear !== this.selectedTemplate.showPriorYear ||
                this.savedSettings.showYearBuilt !== this.selectedTemplate.showYearBuilt ||
                this.savedSettings.showAcquisitionInfo !== this.selectedTemplate.showAcquisitionInfo ||
                this.savedSettings.showAppealDeadlines !== this.selectedTemplate.showAppealDeadlines ||
                this.savedSettings.showAssessments !== this.selectedTemplate.showAssessments ||
                this.savedSettings.showMetrics !== this.selectedTemplate.showMetrics ||
                this.savedSettings.showInitialValue !== this.selectedTemplate.showInitialValue ||
                this.savedSettings.showTargetValue !== this.selectedTemplate.showTargetValue ||
                this.savedSettings.showLatestValue !== this.selectedTemplate.showLatestValue ||
                this.savedSettings.showTaxDetails !== this.selectedTemplate.showTaxDetails ||
                this.savedSettings.showSavings !== this.selectedTemplate.showSavings ||
                this.savedSettings.attachChart !== this.selectedTemplate.attachChart ||
                this.savedSettings.embedInEmail !== this.selectedTemplate.embedInEmail;
        }

        if (hasNotSavedChanges) {
            if (
                (await this._messageBoxService.open({
                    message:
                        'Changes have not been saved to a template and will be lost. Are you sure you would like to close before saving to a template, downloading or sending?',
                    buttons: MessageBoxButtons.YesNo,
                })) === MessageBoxResult.Yes
            ) {
                this._bsModalRef.hide();
            }
        } else {
            this._bsModalRef.hide();
        }
    }

    async refresh(): Promise<void> {
        const busyMsgId = 'refreshing';
        this.busyIndicatorMessageManager.add('Refreshing', busyMsgId);

        try {
            this.appealRecommendationInfo.emailBodyTemplate = UtilitiesService.patchQuillHtml(
                this.mostRecentEmailBodyUpdate ?? this.appealRecommendationInfo.emailBodyTemplate,
            );
            const bodyTemplate = this.appealRecommendationInfo.emailBodyTemplate;

            const model: Core.AppealRecommendationTemplateRefreshModel = {
                taskIds: this.taskIds,
                emailSubjectTemplate: this.appealRecommendationInfo.emailSubjectTemplate,
                emailBodyTemplate: bodyTemplate,
                showPriorYear: this.savedSettings.showPriorYear,
                embedInEmail: this.savedSettings.embedInEmail,
                includeSiteComment: this.savedSettings.includeSiteComment,
                recipientFirstNames: this.appealRecommendationInfo.toRecipients.map((x) => x.name.split(' ')[0]),
                showAssessments: this.savedSettings.showAssessments,
                showMetrics: this.savedSettings.showMetrics,
                showInitialValue: this.savedSettings.showInitialValue,
                showTargetValue: this.savedSettings.showTargetValue,
                showLatestValue: this.savedSettings.showLatestValue,
                showTaxDetails: this.savedSettings.showTaxDetails,
                showSavings: this.savedSettings.showSavings,
                showYearBuilt: this.savedSettings.showYearBuilt,
                showAcquisitionInfo: this.savedSettings.showAcquisitionInfo,
                showAppealDeadlines: this.savedSettings.showAppealDeadlines,
                attachChart: this.savedSettings.attachChart,
                showTaxableValues: this.savedSettings.showTaxableValues,
                showEmptyColumns: this.savedSettings.showEmptyColumns,
                showExemptions: this.savedSettings.showExemptions,
            };

            const result = await lastValueFrom(this._appealRecommendationRepository.refresh(model));

            this.appealRecommendationInfo.emailSubject = result.emailSubject;
            this.appealRecommendationInfo.emailBody = result.emailBody;
            this.emailBody = this._sanitizer.sanitize(SecurityContext.HTML, this.appealRecommendationInfo.emailBody);
            this._siteDivisorDescriptors = result.siteDivisors;
        } finally {
            this.busyIndicatorMessageManager.remove(busyMsgId);
        }
    }

    edit() {
        this._appealRecommendationInfo = { ...this.appealRecommendationInfo };
        this._savedSettings = { ...this.savedSettings };
        this._attachments = this.attachments.slice(0);
        this.editing = true;

        // Previously-saved templates may contain HTML with known Quill 2.0 bugs (e.g. bare
        // <div></div> or unpatched &nbsp; runs) that were never normalized before this point.
        // Patch them here so the editor doesn't have to parse/re-render invalid HTML, which can
        // hang the browser. Apply the same patch to _initialAppealRecommendationInfo so close()'s
        // unsaved-changes comparison isn't thrown off by this normalization alone.
        this.appealRecommendationInfo.emailBodyTemplate = UtilitiesService.patchQuillHtml(
            this.appealRecommendationInfo.emailBodyTemplate,
        );
        this._initialAppealRecommendationInfo.emailBodyTemplate = this.appealRecommendationInfo.emailBodyTemplate;
        this.mostRecentEmailBodyUpdate = this.appealRecommendationInfo.emailBodyTemplate;

        this._selectedTemplateId = this.selectedTemplate.appealRecommendationEmailTemplateId;
    }

    changeTab(tab: PanelView): void {
        this.selectedView = tab;
    }

    addBindingCreated(quillInstance: any): void {
        this.editorInstance = quillInstance;
    }

    insertFieldInSubject(field: TemplateFieldDTO): void {
        const templateSubject = $('#template-subject');
        const cursorPosStart = templateSubject.prop('selectionStart');
        const cursorPosEnd = templateSubject.prop('selectionEnd');
        const v = templateSubject.val();
        const textBefore = v.substring(0, cursorPosStart);
        const textAfter = v.substring(cursorPosEnd, v.length);
        const fieldName = `{{ ${field.name} }}`;

        templateSubject.val(textBefore + fieldName + textAfter);
        this.appealRecommendationInfo.emailSubjectTemplate = templateSubject.val();

        templateSubject.focus();
    }

    insertFieldInBody(field: TemplateFieldDTO): void {
        const cursorPosition: number = this.editorInstance.getSelection(true).index;
        const fieldName: string = `{{ ${field.name} }}`;

        this.editorInstance.insertText(cursorPosition, fieldName);
        this._timer.setTimeout(() => this.editorInstance.setSelection(cursorPosition + fieldName.length), 1);
    }

    // Inserting field does not trigger binding change event, so we need to manually update the model
    contentChange(change: ContentChange): void {
        // #457535 / #587257 - only track the latest edit while actively editing; don't commit it
        // to appealRecommendationInfo.emailBodyTemplate here, since that would re-trigger writeValue()
        // via [(ngModel)] and can loop indefinitely for certain HTML content. The committed value is
        // applied once, explicitly, in refresh() when the user saves.
        if (this.editing) {
            this.mostRecentEmailBodyUpdate = change.html;
        }
    }

    async downloadEmail(): Promise<void> {
        const busyMsgId = 'downloading';
        this.busyIndicatorMessageManager.add('Generating the .eml file', busyMsgId);

        try {
            const model: Core.AppealRecommendationEmailModel = {
                emailSubject: this.appealRecommendationInfo.emailSubject,
                emailBody: this.appealRecommendationInfo.emailBody,
                toIds: this.appealRecommendationInfo.toRecipients.map((x) => x.value),
                ccIds: this.appealRecommendationInfo.ccRecipients?.length
                    ? this.appealRecommendationInfo.ccRecipients.map((x) => x.value)
                    : null,
                taskIds: this.taskIds,
                options: this.savedSettings,
                siteAttachments: this.attachments,
            };

            const response = await lastValueFrom(this._appealRecommendationRepository.download(model, this.files));
            await this._downloadFileService.downloadResponse(response);

            this._initialAppealRecommendationInfo = this.appealRecommendationInfo;

            await this._saveSettings();
        } finally {
            this.busyIndicatorMessageManager.remove(busyMsgId);
        }
    }

    async sendEmail(): Promise<void> {
        let recipients = this.appealRecommendationInfo.toRecipients.map((x) => x.name).join(', ');

        if (this.appealRecommendationInfo.ccRecipients?.length) {
            recipients += ` and ${this.appealRecommendationInfo.ccRecipients.map((x) => x.name).join(', ')}`;
        }

        if (
            (await this._messageBoxService.open({
                message: `An appeal recommendation email will be sent to ${recipients}. Are you sure?`,
                buttons: MessageBoxButtons.YesNo,
            })) === MessageBoxResult.Yes
        ) {
            const busyMsgId = 'sending';
            this.busyIndicatorMessageManager.add('Sending email', busyMsgId);

            try {
                const model: Core.AppealRecommendationEmailModel = {
                    emailSubject: this.appealRecommendationInfo.emailSubject,
                    emailBody: this.appealRecommendationInfo.emailBody,
                    toIds: this.appealRecommendationInfo.toRecipients.map((x) => x.value),
                    ccIds: this.appealRecommendationInfo.ccRecipients?.length
                        ? this.appealRecommendationInfo.ccRecipients.map((x) => x.value)
                        : null,
                    taskIds: this.taskIds,
                    options: this.savedSettings,
                    siteAttachments: this.attachments,
                };

                await this._appealRecommendationRepository.send(model, this.files);

                await this._saveSettings();

                this._bsModalRef.hide();
                this._toastsManager.success('Your email has been sent');
            } finally {
                this.busyIndicatorMessageManager.remove(busyMsgId);
            }
        }
    }

    getCheckboxHelpContentId(): string {
        return this.editing ? null : 'appeal-recommendation.checkbox.disabled';
    }

    async save(): Promise<void> {
        await this.refresh();
        this.editing = false;
    }

    cancel(): void {
        this.appealRecommendationInfo = this._appealRecommendationInfo;
        this.savedSettings = this._savedSettings;
        this.mostRecentEmailBodyUpdate = null;

        this.selectedTemplate = this.templates.find(
            (x) => x.appealRecommendationEmailTemplateId === this._selectedTemplateId,
        );
        if (!this.selectedTemplate) {
            this.selectedTemplate = this.templates[0];
        }

        this.editing = false;
        this.attachments = this._attachments;
    }

    async commentSiteChanged(siteId: number): Promise<void> {
        const modalData: CommentModalData = {
            canEdit: true,
            commentTypeID: CommentTypeNames['Appeal Recommendation'],
            entityTypeID: EntityTypeIds.SITE,
            entityID: siteId,
            description: `Site: ${this.sites.find((x) => x.siteId === siteId).siteName}`,
            year: this.appealRecommendationInfo.taxYear.toString(),
            disableYearSelection: true,
            disableCommentTypeSelection: true,
        };

        await this._commentsModalService.openCommentModal(modalData);
    }

    onFilesDropped(files: File[]): void {
        this._arTemplateService.addFiles(files);
    }

    get canSave(): boolean {
        return (
            !!this.appealRecommendationInfo.toRecipients?.length &&
            (this.appealRecommendationInfo.emailSubjectTemplate || '').trim().length !== 0
        );
    }

    get editHelpContentId(): string {
        if (!this.selectedTemplate) {
            return 'appeal-recommendation-template-specification.no-template';
        }

        if (this.selectedTemplate.isDefault) {
            return 'appeal-recommendation-template-specification.edit-default';
        }

        if (this.selectedTemplate.isShared && !this._hasEditSystemSearchPermission) {
            return 'app.save-disabled';
        }

        return 'app.save';
    }

    get copyHelpContentId(): string {
        if (!this.selectedTemplate) {
            return 'appeal-recommendation-template-specification.no-template';
        }

        return 'appeal-recommendation-template-specification.copy';
    }

    get deleteHelpContentId(): string {
        if (!this.selectedTemplate) {
            return 'appeal-recommendation-template-specification.no-template';
        }

        if (this.selectedTemplate.isDefault) {
            return 'appeal-recommendation-template-specification.delete-default';
        }

        if (this.selectedTemplate.isShared && !this._hasEditSystemSearchPermission) {
            return 'app.delete-disabled';
        }

        return 'app.delete';
    }

    async editTemplate(copy: boolean): Promise<void> {
        const params: AppealRecommendationTemplateDetailsParams = {
            emailSubjectTemplate: this.appealRecommendationInfo.emailSubjectTemplate,
            // Use the tracked in-progress edit (if any) so a pending, unsaved body change isn't
            // silently dropped when saving directly to the template via the header save icon.
            emailBodyTemplate: this.mostRecentEmailBodyUpdate ?? this.appealRecommendationInfo.emailBodyTemplate,
            options: this.savedSettings,
            taskIds: this.taskIds,
            existingTemplates: this.templates,
            selectedTemplate: copy ? null : this.selectedTemplate,
        };

        const result: Core.AppealRecommendationEmailTemplateModel = await this._modalService.showAsync(
            AppealRecommendationTemplateDetailsComponent,
            params,
            'modal-md',
        );

        if (!result) {
            return Promise.resolve();
        }

        if (copy) {
            const savedTemplate = this._convertToListItem(result);

            this.templates.push(savedTemplate);
            this.selectedTemplate = savedTemplate;
        } else {
            const updatedTemplateIndex = this.templates.findIndex(
                (x) => x.appealRecommendationEmailTemplateId === result.appealRecommendationEmailTemplateId,
            );
            const updatedTemplate = { ...this.templates[updatedTemplateIndex], ...result };
            this.templates[updatedTemplateIndex] = updatedTemplate;
            this.selectedTemplate = updatedTemplate;
        }

        this._initialAppealRecommendationInfo.emailSubjectTemplate = this.selectedTemplate.emailSubjectTemplate;
        this._initialAppealRecommendationInfo.emailBodyTemplate = this.selectedTemplate.emailBodyTemplate;

        this._sortSavedTemplates();
        await this.save();
    }

    async deleteTemplate(): Promise<void> {
        try {
            await this._messageModalService.confirm(
                `Template will be permanently deleted. Are you sure you wish to delete the template "${this.selectedTemplate.name}"?`,
                'Confirm Delete',
            );
        } catch (e) {
            return Promise.resolve();
        }

        const busyMsgId = 'deletingTemplate';
        this.busyIndicatorMessageManager.add('Deleting template', busyMsgId);

        const templateId = this.selectedTemplate.appealRecommendationEmailTemplateId;
        try {
            await lastValueFrom(this._appealRecommendationRepository.deleteTemplate(templateId));
        } finally {
            this.busyIndicatorMessageManager.remove(busyMsgId);
        }

        this.selectedTemplate = this.templates[0];
        this._selectedTemplateId = this.selectedTemplate.appealRecommendationEmailTemplateId;
        await this.onTemplateChanged();
        const deletedTemplateIndex = this.templates.findIndex(
            (x) => x.appealRecommendationEmailTemplateId === templateId,
        );
        this.templates.splice(deletedTemplateIndex, 1);
        this.editing = false;
    }

    async onTemplateChanged(): Promise<void> {
        if (this.appealRecommendationInfo) {
            this.appealRecommendationInfo.emailSubjectTemplate = this.selectedTemplate.emailSubjectTemplate;
            this.appealRecommendationInfo.emailBodyTemplate = this.selectedTemplate.emailBodyTemplate;
        }
        // Discard any pending in-progress edit tracked by contentChange() so refresh() below uses
        // the newly-selected template's body rather than a stale edit from a previous template.
        this.mostRecentEmailBodyUpdate = null;

        this.savedSettings.includeSiteComment = this.selectedTemplate.includeSiteComment;
        this.savedSettings.showPriorYear = this.selectedTemplate.showPriorYear;
        this.savedSettings.showYearBuilt = this.selectedTemplate.showYearBuilt;
        this.savedSettings.showAcquisitionInfo = this.selectedTemplate.showAcquisitionInfo;
        this.savedSettings.showAppealDeadlines = this.selectedTemplate.showAppealDeadlines;
        this.savedSettings.showAssessments = this.selectedTemplate.showAssessments;
        this.savedSettings.showMetrics = this.selectedTemplate.showMetrics;
        this.savedSettings.showInitialValue = this.selectedTemplate.showInitialValue;
        this.savedSettings.showTargetValue = this.selectedTemplate.showTargetValue;
        this.savedSettings.showLatestValue = this.selectedTemplate.showLatestValue;
        this.savedSettings.showTaxDetails = this.selectedTemplate.showTaxDetails;
        this.savedSettings.showSavings = this.selectedTemplate.showSavings;
        this.savedSettings.embedInEmail = this.selectedTemplate.embedInEmail;
        this.savedSettings.attachChart = this.selectedTemplate.attachChart;
        this.savedSettings.showEmptyColumns = this.selectedTemplate.showEmptyColumns;

        if (this.initialized) {
            await this.refresh();

            // Quill may fire onContentChanged asynchronously in response to the template swap above
            // (not user typing), which could repopulate mostRecentEmailBodyUpdate with Quill's own
            // re-serialized HTML while editing is still true. Null it again after refresh() so a
            // subsequent close() doesn't report a false "unsaved changes" prompt from switching
            // templates alone.
            this.mostRecentEmailBodyUpdate = null;
            this._initialAppealRecommendationInfo = { ...this.appealRecommendationInfo };
        }
    }

    get canEditDelete(): boolean {
        return (
            !this.selectedTemplate.isDefault &&
            (!this.selectedTemplate.isShared || (this.selectedTemplate.isShared && this._hasEditSystemSearchPermission))
        );
    }

    private async _loadSettings(): Promise<void> {
        const settingsGroup = this._userSettingsService.getSettingsByGroup(
            Core.UserSettingGroup.AppealRecommendationTemplate,
        );
        this._savedUserSettings = settingsGroup.find((x) => x.name === this._settingName);

        if (this._savedUserSettings && this._savedUserSettings.value) {
            const savedTemplateId = +this._savedUserSettings.value;

            if (!isNaN(savedTemplateId)) {
                const savedTemplate = this.templates.find(
                    (x) => x.appealRecommendationEmailTemplateId === savedTemplateId,
                );

                this.selectedTemplate = savedTemplate || this.templates[0];
            } else {
                this.selectedTemplate = this.templates[0];
            }

            this.savedSettings.showTaxableValues = true;

            await this.onTemplateChanged();
        } else {
            this.selectedTemplate = this.templates[0];
            await this.onTemplateChanged();
        }
    }

    private async _saveSettings() {
        if (!this._savedUserSettings) {
            this._savedUserSettings = {
                id: 0,
                name: this._settingName,
                groupId: Core.UserSettingGroup.AppealRecommendationTemplate,
                folderId: Core.UserSettingFolder.Default,
                value: {},
            };
        }

        this._savedUserSettings.value = this.selectedTemplate.appealRecommendationEmailTemplateId;

        this._savedUserSettings = await this._userSettingsService.save(this._savedUserSettings);
    }

    private _setConsistentPanelWidth(): void {
        if (!this.panelSize && this._optionsPanel1?.nativeElement && this._optionsPanel2?.nativeElement) {
            this.panelSize = null;
            this._cdRef.detectChanges();
            const p1 = this._optionsPanel1.nativeElement.getBoundingClientRect();
            const p2 = this._optionsPanel2.nativeElement.getBoundingClientRect();
            this.panelSize = {
                width: `${p1.width > p2.width ? p1.width : p2.width}px`,
                height: `${p1.height > p2.height ? p1.height : p2.height}px`,
            };
            this._cdRef.detectChanges();
        }
    }

    private _sortSavedTemplates(): void {
        const defaultTemplates = this.templates.filter((x) => x.isDefault);
        defaultTemplates.sort((x, y) => x.name.localeCompare(y.name));

        const customTemplates = this.templates.filter((x) => !x.isDefault);
        customTemplates.sort((x, y) => x.name.localeCompare(y.name));

        this.templates = defaultTemplates.concat(customTemplates);
    }

    private _convertToListItem(
        x: Core.AppealRecommendationEmailTemplateModel,
    ): AppealRecommendationEmailTemplateListItemModel {
        const result = {
            ...x,
            ...{
                visibilityItem: {
                    isShared: x.isShared,
                    isDefault: x.isDefault,
                    name: '',
                    description: '',
                },
                canEdit: false,
            },
        };

        if (result.isDefault) {
            result.visibilityItem.name = 'Default';
            result.visibilityItem.description = 'Visible to everyone';
        } else if (result.isShared) {
            result.visibilityItem.name = 'Shared';
            result.visibilityItem.description = 'Visible to everyone';
        } else {
            result.visibilityItem.name = 'Private';
            result.visibilityItem.description = 'Visible only to you';
        }

        return result;
    }
}
